import type { Span } from "@opentelemetry/sdk-trace-web";

type SessionAttributeValue = string | number | boolean;

const sessionAttributes = new Map<string, SessionAttributeValue>();

/**
 * Mutable session context (workspace id, user id, workspace-size bucket, storage backend) stamped
 * onto every span at start. Unlike OTel resource attributes these can change mid-session — e.g. a
 * workspace switch — and unlike per-span attributes callers don't have to thread them everywhere.
 */
export function setSessionAttribute(key: string, value: SessionAttributeValue | undefined): void {
  if (value === undefined) {
    sessionAttributes.delete(key);
    return;
  }
  sessionAttributes.set(key, value);
}

export function setSessionAttributes(attributes: Record<string, SessionAttributeValue | undefined>): void {
  for (const [key, value] of Object.entries(attributes)) {
    setSessionAttribute(key, value);
  }
}

/** Test-only reset so module-level session state can't leak between tests. */
export function resetSessionAttributesForTests(): void {
  sessionAttributes.clear();
}

/** Span processor that stamps the current session attributes onto every span at start. */
export class SessionAttributesSpanProcessor {
  public onStart(span: Span): void {
    for (const [key, value] of sessionAttributes.entries()) {
      span.setAttribute(key, value);
    }
  }

  public onEnd(): void {}

  public forceFlush(): Promise<void> {
    return Promise.resolve();
  }

  public shutdown(): Promise<void> {
    return Promise.resolve();
  }
}
